import * as types from 'config/actionTypes'
import api, { urls } from 'config/api'
import {
  CURRENCY_LOCAL_STORAGE,
  INCOGNITO_LOCAL_STORAGE,
  TOKEN_EXP_LOCAL_STORAGE,
  TOKEN_KEY_LOCAL_STORAGE,
  TRACKING_KEY,
} from 'config/constants'
import moment from 'moment-timezone'
import { getDuration } from 'utils'

/**
 * Action creators
 */
const _login = () => ({ type: types.LOG_IN })
const _logout = () => ({ type: types.LOG_OUT })
const _setUserInfo = userInfo => ({ type: types.SET_USER_INFO, userInfo })
const _setTracking = tracking => ({
  type: types.SET_TRACKING,
  tracking,
})
const _setIncognito = incognito => ({ type: types.SET_INCOGNITO, incognito })
const _setCurrency = currency => ({ type: types.SET_CURRENCY, currency })

/**
 * Actions
 */
export const startup = () => async dispatch => {
  if (localStorage.getItem(TOKEN_KEY_LOCAL_STORAGE)) {
    await Promise.all([
      dispatch(getUserInfo()),
      dispatch(getTrackingStartTime()),
    ])
    dispatch(_login())
  }
}

export const ssoStart = token => async () => {
  const resp = await api.post(urls.ssoStart(), {
    token,
  })
  const expiration = new Date()
  expiration.setTime(expiration.getTime() + 86400 * 1000)
  localStorage.setItem(TOKEN_KEY_LOCAL_STORAGE, resp.data.token)
  localStorage.setItem(TOKEN_EXP_LOCAL_STORAGE, expiration)
}

export const login = (username, password) => async dispatch => {
  try {
    const resp = await api.post(urls.login(), {
      username,
      password,
    })
    localStorage.setItem(TOKEN_KEY_LOCAL_STORAGE, resp.data.token)
    await dispatch(startup())
    await dispatch(_login())
  } catch (err) {
    // clear all state if we hit an error in the process
    dispatch(_logout())
    console.error('Error logging in')
    console.error(err)
    throw err
  }
}

export const logout =
  (clearStorage = true) =>
  async () => {
    // Clear local storage if the user logged out. If the user was forced
    // out e.g. by an expired SSO session, keep the local storage in case
    // they were tracking a session.
    if (clearStorage) {
      localStorage.removeItem(TOKEN_KEY_LOCAL_STORAGE)
      localStorage.removeItem(TRACKING_KEY)
    }
  }

export const getUserInfo = () => async dispatch => {
  const resp = await api.get(urls.user())
  dispatch(_setUserInfo(resp.data))
  return resp.data
}

export const startTracking = () => async dispatch => {
  const time = moment.tz('UTC').format()
  const tracking = [time]
  localStorage.setItem(TRACKING_KEY, JSON.stringify(tracking))
  dispatch(_setTracking(tracking))
}

export const getTrackingStartTime = () => async dispatch => {
  let tracking = localStorage.getItem(TRACKING_KEY)
  if (tracking === null) return
  try {
    // try parsing as JSON – this is how we now store it
    tracking = JSON.parse(tracking)
  } catch (err) {
    // treat is as a single start time - this is how we used to store it
    tracking = [tracking]
  }
  dispatch(_setTracking(tracking))
}

export const pauseOrResumeTracking = () => async (dispatch, getState) => {
  const time = moment.tz('UTC').format()
  const tracking = [...getState().settings.tracking, time]
  localStorage.setItem(TRACKING_KEY, JSON.stringify(tracking))
  dispatch(_setTracking(tracking))
}

export const stopTracking = () => async (dispatch, getState) => {
  const tracking = getState().settings.tracking
  const duration = getDuration(tracking)
  localStorage.removeItem(TRACKING_KEY)
  dispatch(_setTracking(null))
  return duration
}

export const updateIncognito = incognito => async dispatch => {
  dispatch(_setIncognito(incognito))
  if (incognito) {
    localStorage.setItem(INCOGNITO_LOCAL_STORAGE, incognito)
  } else {
    localStorage.removeItem(INCOGNITO_LOCAL_STORAGE)
  }
}

export const updateCurrency = currency => async dispatch => {
  dispatch(_setCurrency(currency))
  if (currency) {
    localStorage.setItem(CURRENCY_LOCAL_STORAGE, currency)
  } else {
    localStorage.removeItem(CURRENCY_LOCAL_STORAGE)
  }
}
